// Filter state helpers. Dates are plain calendar days ("2026-09-12") so that a
// "today in Budapest" filter means the same thing from any timezone.
// Shared verbatim with mobile/utils/filters.js.

import { toDayKey } from './format';

export const EMPTY_FILTERS = {
  city: undefined,
  country: undefined,
  venue: undefined,
  districts: [],
  categories: [],
  // Genre groups - 'popular' | 'classical' | 'folk' (see utils/musicGroups.js).
  musicGroups: [],
  music_genre: [],
  special_features: [],
  dateFrom: undefined,
  dateTo: undefined,
  quickDate: undefined,
  freeOnly: false
};

function shiftDays(days) {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + days);
  return date;
}

export const QUICK_DATES = ['today', 'tomorrow', 'weekend', 'week'];

// Returns { dateFrom, dateTo } for a preset id, both as calendar days.
export function quickDateRange(id) {
  if (id === 'today') {
    const today = shiftDays(0);
    return { dateFrom: toDayKey(today), dateTo: toDayKey(today) };
  }

  if (id === 'tomorrow') {
    const tomorrow = shiftDays(1);
    return { dateFrom: toDayKey(tomorrow), dateTo: toDayKey(tomorrow) };
  }

  if (id === 'weekend') {
    // Friday (from the evening - see requestDateFrom) through Sunday. From
    // Friday to Sunday that is the weekend under way, starting today; earlier
    // in the week it is the coming one.
    const dayOfWeek = shiftDays(0).getDay(); // 0 = Sunday ... 5 = Friday, 6 = Saturday
    const daysUntilFriday = dayOfWeek === 0 || dayOfWeek >= 5 ? 0 : 5 - dayOfWeek;
    const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
    return {
      dateFrom: toDayKey(shiftDays(daysUntilFriday)),
      dateTo: toDayKey(shiftDays(daysUntilSunday))
    };
  }

  if (id === 'week') {
    return { dateFrom: toDayKey(shiftDays(0)), dateTo: toDayKey(shiftDays(7)) };
  }

  return { dateFrom: undefined, dateTo: undefined };
}

// "This weekend" starts on Friday evening: a Friday 19:00 concert belongs to
// it, a Friday 9:30 toddler class does not. The filter itself keeps plain days
// (the date pickers display them), so the evening start is added only to the
// dateFrom the API receives.
export const WEEKEND_START_TIME = '17:00';

export function requestDateFrom(filters = {}) {
  const { dateFrom, quickDate } = filters;
  if (quickDate === 'weekend' && dateFrom && new Date(`${dateFrom}T12:00`).getDay() === 5) {
    return `${dateFrom} ${WEEKEND_START_TIME}`;
  }
  return dateFrom;
}

export function applyQuickDate(filters, id) {
  if (filters.quickDate === id) {
    return { ...filters, dateFrom: undefined, dateTo: undefined, quickDate: undefined };
  }
  return { ...filters, ...quickDateRange(id), quickDate: id };
}

export function toggleInList(list = [], value) {
  return list.includes(value)
    ? list.filter((item) => item !== value)
    : [...list, value];
}

function isSet(value) {
  if (value === undefined || value === null || value === false || value === '') {
    return false;
  }
  return Array.isArray(value) ? value.length > 0 : true;
}

// Number of filter facets in use - drives the badge on the Filters button.
export function countActiveFilters(filters = {}) {
  let count = 0;

  for (const key of ['city', 'country', 'venue', 'freeOnly']) {
    if (isSet(filters[key])) {
      count += 1;
    }
  }
  for (const key of ['districts', 'categories', 'musicGroups', 'music_genre', 'special_features']) {
    count += (filters[key] || []).length;
  }
  if (isSet(filters.dateFrom) || isSet(filters.dateTo)) {
    count += 1;
  }

  return count;
}

export function hasAnyFilter(filters = {}, searchQuery = '') {
  return Boolean(searchQuery.trim()) || countActiveFilters(filters) > 0;
}
